import { useMemo, useState } from "react";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import PureInput from "@/components/common/form/PureInput";
import useTariffs from "@/common/hook/dashboard/useTariffs";
import PureSelect from "@/components/common/form/PureSelect";
import InputRadio from "@/components/common/form/InputRadio";

const TARIFFS = Array.from({ length: 20 }, (v, i) => (i + 1) * 10);

const FindSalary = () => {
  const { t } = useTranslation("become-consultant");
  const minuteTariffs = useTariffs();
  const [minutePerWeek, setMinutePerWeek] = useState(30);
  const [pricePer, setPricePer] = useState(1.99 / 1.19);
  const [multiple, setMultiple] = useState("1");

  const tariffs = useMemo(() => {
    if (multiple === "1") {
      return minuteTariffs?.map((tariff) => tariff.centsPerMinute / 100) || [];
    }

    return TARIFFS;
  }, [multiple, minuteTariffs]);

  /* mt-16 and sm:mt-24 for default main margin. Because removed margin bottom */
  return (
    <section className="py-24 mt-16 sm:mt-24 bg-purple-800">
      <div className="lg:box-content flex flex-col items-center px-4 lg:px-8 mx-auto w-full lg:w-[826px]">
        <div className="flex w-full h-16 font-semibold bg-white rounded-full">
          <div className="w-[70%] h-full text-white bg-blue rounded-full flex-center">
            <span className="text-xl">{t`find-salary.for-you`}</span>
            <span className="ml-3 text-3xl">70%</span>
          </div>
          <div className="flex-grow text-grey-700 flex-center">
            <span className="text-xs lg:text-base">{t`find-salary.for-us`}</span>
            <span className="ml-1 lg:ml-3 text-xs lg:text-xl">30%</span>
          </div>
        </div>
        <p className="mt-6 text-xs lg:text-sm text-center text-grey-700">{t`find-salary.subtext`}</p>

        <h2 className="mt-16 lg:text-xl font-semibold text-center">{t`find-salary.title`}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-24 gap-y-16 justify-items-center items-start py-6 lg:py-9 px-16 lg:px-24 mt-6 w-full font-semibold text-center rounded-lg border border-purple">
          <div className="w-full">
            <label className="text-grey-800" htmlFor="minutesOfConversation">
              {t`find-salary.minutes-of-conversation`}
            </label>
            <PureInput
              register={() => ({
                onChange: ({ target }) => setMinutePerWeek(target.value.replace(/\D/, "")),
                value: minutePerWeek.toString(),
              })}
              id="minutesOfConversation"
              className="mt-8 w-full"
              type="number"
              min={1}
            />
          </div>
          <div className="w-full">
            <label className="text-grey-800" htmlFor="tariff">
              {t`find-salary.price-per.title`}
            </label>
            <div className="flex gap-3 justify-center mt-3 text-xs">
              <InputRadio
                id="minute"
                register={() => ({
                  name: "calculateType",
                  onChange: ({ target }) => {
                    setMultiple(target.value);
                    setPricePer(0.99 / 1.19);
                  },
                  checked: multiple === "1",
                })}
                value="1"
                label={t`find-salary.price-per.minute`}
              />
              <InputRadio
                id="hour"
                register={() => ({
                  name: "calculateType",
                  onChange: ({ target }) => {
                    setMultiple(target.value);
                    setPricePer(10 / 1.19);
                  },
                  checked: multiple === "60",
                })}
                label={t`find-salary.price-per.hour`}
                value="60"
              />
            </div>
            <PureSelect
              className="mt-6 w-full h-11"
              register={() => ({
                onChange: ({ target }) => setPricePer(target.value),
                defaultValue: 1.99 / 1.19,
              })}
              id="tariff"
              fit
            >
              {tariffs.map((tariff) => (
                <option key={tariff} value={tariff / 1.19}>
                  {tariff} &euro;
                </option>
              ))}
            </PureSelect>
          </div>
          <div>
            <h3 className="text-grey-800">{t`find-salary.your-salary`}</h3>
            <p className="mt-8 text-3xl text-blue">
              {(((minutePerWeek * 4 * pricePer) / multiple) * (70 / 100)).toFixed(2)} €
            </p>
          </div>
        </div>

        <p className="px-9 lg:px-36 mt-6 text-sm lg:text-xl text-center text-grey-800">{t`find-salary.part-of-vialantis`}</p>

        <Link href="/register/start" passHref>
          <button type="button" className="mt-12 lg:mt-16 button-lg hover-orange">
            {t`start-now`}
          </button>
        </Link>
      </div>
    </section>
  );
};

export default FindSalary;
